import {FC, useEffect, useState, useMemo, useRef, useCallback} from 'react'
import clsx from 'clsx'
import {ID, QUERIES, UserAvatar} from '../../../../../_metronic/helpers'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {useTheme, styled} from '@mui/material/styles'
import Popper from '@mui/material/Popper'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import CloseIcon from '@mui/icons-material/Close'
import DoneIcon from '@mui/icons-material/Done'
import Autocomplete, {
  AutocompleteCloseReason,
  autocompleteClasses,
} from '@mui/material/Autocomplete'
import InputBase from '@mui/material/InputBase'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import {useQueryClient, useQuery} from 'react-query'
import {taskresponsible_adjust} from './TaskUsers/core/_requests'
import {taskTemplateResponsibleAdjust} from '../../../task-templates/core/_requests'
import {Placement} from '@popperjs/core'
import {useAuth} from '../../../auth'
import {getTeamByInstance} from '../../../team/components/teams-list/core/_requests'

export interface IconUserModel {
  name: string
  avatar?: string
  color?: string
  initials?: string
  task_responsible_id?: ID
  user_id: ID
  expected_date?: string | null
}

interface TeamOption {
  id: number
  name: string
  leader_id?: number
  leader?: { id: number; name: string }
  user?: Array<{
    id: number
    name: string
    avatar?: string
    job_role?: string
  }>
  isTeam: true
}

type AutocompleteOption = IconUserModel | TeamOption

export type UsersSymbolGroupColumnProps = {
  users: Array<IconUserModel>
  usersAll: Array<IconUserModel>
  classes?: string
  taskId?: ID
  // Quando `taskId` ainda não existe (linha placeholder), permite selecionar usuários
  // e persistir essa seleção no componente pai, para aplicar após a criação.
  placeholderSelectedIds?: number[]
  onPlaceholderSave?: (ids: number[]) => void
  isReadonly?: boolean
  isTemplate?: boolean
  isKanban?: boolean
  placement?: Placement
  // Densidade compacta das 4 rotas de listagem de tarefas em tabela (/mytasks, /tarefas,
  // /tasksbyuser, /projects/:id/tasks) — não afeta Kanban/modais/templates, que continuam
  // com o tamanho padrão.
  compact?: boolean
}

interface PopperComponentProps {
  anchorEl?: any
  disablePortal?: boolean
  open: boolean
}

const StyledAutocompletePopper = styled('div')(({theme}) => ({
  [`& .${autocompleteClasses.paper}`]: {
    boxShadow: 'none',
    margin: 0,
    color: 'inherit',
    fontSize: 13,
    width: 300,
  },
  [`& .${autocompleteClasses.listbox}`]: {
    backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1c2128',
    padding: 0,
    cursor: 'pointer',
    '& *': {
      cursor: 'pointer !important',
    },
    [`& .${autocompleteClasses.option}`]: {
      minHeight: 'auto',
      alignItems: 'flex-start',
      padding: 8,
      borderBottom: `1px solid  ${theme.palette.mode === 'light' ? ' #eaecef' : '#30363d'}`,
      cursor: 'pointer !important',
      userSelect: 'none',
      pointerEvents: 'auto !important',
      '&[aria-selected="true"]': {
        backgroundColor: 'transparent',
        cursor: 'pointer !important',
      },
      '&[aria-disabled="true"]': {
        cursor: 'pointer !important',
        opacity: 1,
        pointerEvents: 'auto !important',
      },
      '&:active, &:focus, &:hover': {
        cursor: 'pointer !important',
      },
      [`&.${autocompleteClasses.focused}, &.${autocompleteClasses.focused}[aria-selected="true"]`]:
        {
          backgroundColor: theme.palette.action.hover,
          cursor: 'pointer !important',
        },
    },
  },
  [`&.${autocompleteClasses.popperDisablePortal}`]: {
    position: 'relative',
  },
}))

function PopperComponent(props: PopperComponentProps) {
  const {disablePortal, anchorEl, open, ...other} = props
  return <StyledAutocompletePopper {...other} />
}
//@ts-ignore
const StyledPopper = styled(Popper)(({theme}) => ({
  border: `1px solid ${theme.palette.mode === 'light' ? '#e1e4e8' : '#30363d'}`,
  boxShadow: `0 8px 24px ${
    theme.palette.mode === 'light' ? 'rgba(149, 157, 165, 0.2)' : 'rgb(1, 4, 9)'
  }`,
  borderRadius: 6,
  width: 300,
  zIndex: theme.zIndex.modal,
  fontSize: 13,
  color: theme.palette.mode === 'light' ? '#24292e' : '#c9d1d9',
  backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1c2128',
}))

const StyledInput = styled(InputBase)(({theme}) => ({
  padding: 10,
  width: '100%',
  borderBottom: `1px solid ${theme.palette.mode === 'light' ? '#eaecef' : '#30363d'}`,
  '& input': {
    borderRadius: 4,
    backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#0d1117',
    padding: 8,
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    border: `1px solid ${theme.palette.mode === 'light' ? '#eaecef' : '#30363d'}`,
    fontSize: 14,
    '&:focus': {
      boxShadow: '0px 0px 0px 3px rgba(181, 212, 78, 0.35)',
      borderColor: 'var(--tr-green-active)',
    },
  },
}))

const UsersSymbolGroupColumn: FC<UsersSymbolGroupColumnProps> = ({
  users,
  usersAll,
  classes,
  taskId,
  placeholderSelectedIds = [],
  onPlaceholderSave,
  isReadonly = false,
  isKanban = false,
  isTemplate = false,
  placement = 'top-end',
  compact = false,
}) => {
  const avatarSizeClass = compact ? 'symbol-25px' : 'symbol-35px'
  const triggerRef = useRef<HTMLDivElement>(null)
  const popperContentRef = useRef<HTMLDivElement>(null)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  /** Seleção pendente enquanto o seletor está aberto; só aplicada (API/email) ao fechar ou Salvar. */
  const [pendingSelectedIds, setPendingSelectedIds] = useState<Set<number> | null>(null)
  const [isApplying, setIsApplying] = useState(false)
  /** Texto do campo de busca no Autocomplete; controlado para garantir que o filtro funcione. */
  const [searchInputValue, setSearchInputValue] = useState('')

  const theme = useTheme()
  const {currentUser} = useAuth()
  const queryClient = useQueryClient()
  const open = Boolean(anchorEl)
  const effectiveAnchor = open ? (triggerRef.current ?? anchorEl) : null
  const placeholderMode = !taskId

  // IDs dos usuários selecionados no servidor
  const serverSelectedIds = useMemo(() => {
    return new Set(users?.map((u) => Number(u.user_id)) ?? [])
  }, [users])

  // Enquanto o seletor está aberto: usar seleção pendente; senão: servidor (evita disparo de API/email no clique)
  const effectiveSelectedIds = useMemo(() => {
    return pendingSelectedIds !== null ? pendingSelectedIds : serverSelectedIds
  }, [pendingSelectedIds, serverSelectedIds])

  // No modo placeholder, a seleção "salva" vem do pai via `placeholderSelectedIds`.
  useEffect(() => {
    if (!placeholderMode) return
    if (!placeholderSelectedIds || placeholderSelectedIds.length === 0) {
      setPendingSelectedIds(null)
      return
    }
    setPendingSelectedIds(new Set(placeholderSelectedIds))
  }, [placeholderMode, placeholderSelectedIds])

  // Usuários exibidos no trigger:
  // - taskId existe: usa `users` (dados do servidor) como fonte
  // - placeholder: mostra os selecionados usando `usersAll` + `effectiveSelectedIds`
  const displayUsers = useMemo(() => {
    if (placeholderMode) {
      // Dedupe por `user_id` e preserve a ordem de `placeholderSelectedIds`.
      const uniqById = new Map<number, IconUserModel>()
      for (const u of usersAll ?? []) {
        const id = Number((u as any)?.user_id)
        if (!Number.isFinite(id)) continue
        if (!effectiveSelectedIds.has(id)) continue
        if (uniqById.has(id)) continue
        uniqById.set(id, u as unknown as IconUserModel)
      }

      const ordered = (placeholderSelectedIds ?? [])
        .map((id) => uniqById.get(id))
        .filter(Boolean) as IconUserModel[]

      // Evita qualquer efeito colateral se por algum motivo vier acima de 3.
      return ordered.slice(0, 3)
    }
    return users ?? []
  }, [placeholderMode, users, usersAll, effectiveSelectedIds])

  // Buscar times da instância apenas quando o dropdown estiver aberto
  const {data: teamsResponse} = useQuery(
    'teams-by-instance',
    () => getTeamByInstance(),
    {
      enabled: !!currentUser?.instance_id && !isReadonly && open,
      staleTime: 5 * 60 * 1000, // Cache por 5 minutos
      cacheTime: 10 * 60 * 1000, // Manter no cache por 10 minutos
      refetchOnWindowFocus: false,
    }
  )

  // A API retorna { teams: Team[], message: string, errors: boolean }
  const teams = teamsResponse?.teams 
    ? (Array.isArray(teamsResponse.teams) 
        ? teamsResponse.teams 
        : [teamsResponse.teams])
    : []

  // Criar estrutura de dados combinada (times + usuários)
  const combinedOptions = useMemo(() => {
    const options: AutocompleteOption[] = []
    const usersInTeams = new Set<number>()
    
    // Adicionar times primeiro, seguidos pelos seus usuários
    teams.forEach((team) => {
      if (team.user && Array.isArray(team.user) && team.user.length > 0) {
        // Adicionar o time
        options.push({
          id: team.id!,
          name: team.name,
          leader_id: (team as any).leader_id,
          leader: (team as any).leader,
          user: team.user.map((u: any) => ({
            id: u.id,
            name: u.name,
            avatar: u.avatar,
            job_role: u.job_role,
          })),
          isTeam: true,
        })

        // Adicionar os usuários do time logo após o time
        team.user.forEach((user: any) => {
          usersInTeams.add(user.id)
          // Buscar o usuário completo de usersAll para manter todas as propriedades
          const fullUser = usersAll?.find((u) => Number(u.user_id) === user.id)
          if (fullUser) {
            options.push(fullUser)
          } else {
            // Se não encontrar em usersAll, criar um objeto básico
            options.push({
              user_id: user.id,
              name: user.name,
              avatar: user.avatar,
              color: user.job_role,
            })
          }
        })
      }
    })

    // Adicionar usuários que não estão em times
    usersAll?.forEach((user) => {
      if (!usersInTeams.has(Number(user.user_id))) {
        options.push(user)
      }
    })

    return options
  }, [teams, usersAll])

  // Mapear usuários para seus times
  const userToTeamMap = useMemo(() => {
    const map = new Map<number, number>()
    teams.forEach((team) => {
      if (team.user && Array.isArray(team.user)) {
        team.user.forEach((user: any) => {
          map.set(user.id, team.id!)
        })
      }
    })
    return map
  }, [teams])

  // Lista única de usuários (fonte de verdade para o modo search — evita duplicados)
  const allUsersFlat = useMemo(() => usersAll ?? [], [usersAll])

  // Verifica se o nome bate na query: nome inteiro ou alguma palavra começa com a query (case-insensitive)
  const nameMatchesQuery = useCallback((name: string, q: string) => {
    if (!name || !q) return false
    const lower = name.toLowerCase().trim()
    const qLower = q.toLowerCase().trim()
    if (!qLower) return false
    const words = lower.split(/\s+/).filter(Boolean)
    return lower.startsWith(qLower) || words.some((w) => w.startsWith(qLower))
  }, [])

  // Filtro de busca: sem texto = lista completa; com texto = usuários que batem, agrupados por equipe
  const filterOptions = useCallback((options: AutocompleteOption[], state: { inputValue: string }) => {
    const raw = (state.inputValue ?? '').trim()
    const query = raw.toLowerCase()
    if (!query) return options
    if (query.length < 2) return []

    const matchingUsers = allUsersFlat.filter(
      (user) => user.name && nameMatchesQuery(user.name, query)
    )
    if (matchingUsers.length === 0) return []

    // Agrupar por equipe: cada usuário aparece só na primeira equipe a que pertence
    const result: AutocompleteOption[] = []
    const addedUserIds = new Set<number>()

    teams.forEach((team) => {
      if (!team.user || !Array.isArray(team.user) || team.user.length === 0) return
      const teamUserIds = new Set(team.user.map((u: any) => u.id))
      const usersInTeam = matchingUsers.filter(
        (u) => teamUserIds.has(Number(u.user_id)) && !addedUserIds.has(Number(u.user_id))
      )
      usersInTeam.forEach((u) => addedUserIds.add(Number(u.user_id)))
      if (usersInTeam.length > 0) {
        const teamOption: TeamOption = {
          id: team.id!,
          name: team.name,
          leader_id: (team as any).leader_id,
          leader: (team as any).leader,
          user: team.user.map((u: any) => ({
            id: u.id,
            name: u.name,
            avatar: u.avatar,
            job_role: u.job_role,
          })),
          isTeam: true,
        }
        result.push(teamOption)
        result.push(...usersInTeam)
      }
    })

    const standalone = matchingUsers.filter((u) => !addedUserIds.has(Number(u.user_id)))
    result.push(...standalone)
    return result
  }, [allUsersFlat, nameMatchesQuery, teams])

  // Verificar se todos os usuários de um time estão selecionados
  const isTeamFullySelected = useCallback((team: TeamOption): boolean => {
    if (!team.user || team.user.length === 0) return false
    return team.user.every((teamUser) => effectiveSelectedIds.has(teamUser.id))
  }, [effectiveSelectedIds])

  // Verificar se um usuário está selecionado
  const isUserSelected = useCallback((userId: ID): boolean => {
    return effectiveSelectedIds.has(Number(userId))
  }, [effectiveSelectedIds])

  // Invalidar só listagens de tarefas (não invalidar task-users nem usersTask = evita refetch getUsers; UI já atualizada de forma otimista)
  const invalidateQueries = useCallback(() => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        // Listagens (table + kanban): tasks-list, mytasks-list, kanban (projects, sprints, mytasks, tasks-by-user)
        const key = Array.isArray(query.queryKey) ? query.queryKey[0] : query.queryKey
        if (typeof key !== 'string') return false
        if (key === QUERIES.MYTASKS_LIST) return false
        if (key.startsWith(`${QUERIES.TASKS_LIST}-usersTask`)) return false
        if (key.startsWith(`${QUERIES.TASKS_LIST}-task-`)) return false
        if (key.startsWith(`${QUERIES.TASKS_LIST}-project-`)) return false
        if (key.startsWith(QUERIES.TASKS_LIST) && key.endsWith('-users')) return false
        if (key.startsWith(QUERIES.TASKS_LIST) || key.startsWith(QUERIES.MYTASKS_LIST)) return true
        if (key.startsWith('project-tasks-kanban')) return true
        if (key === 'tasks') return true
        if (key.startsWith(QUERIES.SPRINTS_LIST)) return true
        if (key.startsWith('mytasks-kanban')) return true
        if (key.startsWith('tasks-by-user-kanban')) return true
        return false
      },
      refetchInactive: false,
    })
  }, [queryClient])

  // Toggle de usuário: só atualiza seleção pendente (requisição/email ao fechar ou Salvar)
  const handleUserToggle = useCallback((userOption: IconUserModel) => {
    const userId = Number(userOption.user_id)
    setPendingSelectedIds((prev) => {
      if (!prev) return prev
      const next = new Set(prev)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return next
    })
  }, [])

  // Toggle de time: só atualiza seleção pendente (requisição/email ao fechar ou Salvar)
  const handleTeamToggle = useCallback((teamOption: TeamOption) => {
    const teamUsers = teamOption.user
    if (!teamUsers || teamUsers.length === 0) return
    setPendingSelectedIds((prev) => {
      if (!prev) return prev
      const next = new Set(prev)
      const allSelected = teamUsers.every((u) => next.has(u.id))
      teamUsers.forEach((u) => {
        if (allSelected) next.delete(u.id)
        else next.add(u.id)
      })
      return next
    })
  }, [])

  const doClose = useCallback((opts?: {preservePending?: boolean}) => {
    if (anchorEl) anchorEl.focus()
    setAnchorEl(null)
    if (!opts?.preservePending) {
      setPendingSelectedIds(null)
    }
    setSearchInputValue('')
  }, [anchorEl])

  // Aplicar alterações (chamar API) e fechar; só dispara requisição/email aqui
  const applyAndClose = useCallback(async () => {
    if (pendingSelectedIds === null) {
      doClose()
      return
    }

    // Placeholder: não existe task_id ainda, então só persistimos a seleção no pai.
    if (!taskId) {
      onPlaceholderSave?.(Array.from(pendingSelectedIds))
      doClose({preservePending: true})
      return
    }

    const added = Array.from(pendingSelectedIds).filter((id) => !serverSelectedIds.has(id))
    const removed = Array.from(serverSelectedIds).filter((id) => !pendingSelectedIds.has(id))
    if (added.length === 0 && removed.length === 0) {
      doClose()
      return
    }

    setIsApplying(true)
    try {
      const adjust = (userId: number) => {
        if (isTemplate) {
          return taskTemplateResponsibleAdjust({
            user_id: userId,
            task_template_id: taskId,
          })
        }
        return taskresponsible_adjust({user_id: userId, task_id: taskId})
      }
      await Promise.all([...added.map(adjust), ...removed.map(adjust)])
      invalidateQueries()
      // Atualiza a query que alimenta os users deste componente (ex.: modal de tarefa)
      queryClient.invalidateQueries([`${QUERIES.TASKS_LIST}-${taskId}-users`])
    } finally {
      setIsApplying(false)
      doClose()
    }
  }, [
    pendingSelectedIds,
    serverSelectedIds,
    taskId,
    isTemplate,
    invalidateQueries,
    doClose,
    onPlaceholderSave,
    queryClient,
  ])

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isReadonly) return
    // Se o dropdown já estiver aberto, o clique no gatilho deve "aplicar" as mudanças,
    // e não resetar a seleção pendente (caso contrário, o usuário selecionado some).
    if (open) {
      applyAndClose()
      return
    }

    // No modo placeholder, `serverSelectedIds` vem como vazio (task ainda não existe),
    // então a seleção atual deve ser carregada do estado do pai.
    if (placeholderMode) {
      setPendingSelectedIds(new Set(placeholderSelectedIds))
    } else {
      setPendingSelectedIds(new Set(serverSelectedIds))
    }
    setSearchInputValue('')
    setAnchorEl(triggerRef.current ?? event.currentTarget)
  }

  const handleClose = useCallback(() => {
    applyAndClose()
  }, [applyAndClose])

  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    if (isApplying) return
    const target = event.target as Node
    const targetEl = target as unknown as HTMLElement | null
    // Evita fechar/resetar quando o clique/mousedown aconteceu em uma opção do Autocomplete
    // (a seleção pendente deve continuar válida até fechar/aplicar).
    if (targetEl?.closest?.('[data-user-selector-option="1"]')) {
      return
    }
    if (triggerRef.current?.contains(target) || popperContentRef.current?.contains(target)) {
      return
    }
    handleClose()
  }

  const intl = useIntl()

  // Valor para o Autocomplete: quando aberto usa seleção pendente; senão servidor
  const autocompleteValue = useMemo(() => {
    const ids = pendingSelectedIds ?? serverSelectedIds
    return usersAll?.filter((u) => ids.has(Number(u.user_id))) ?? []
  }, [usersAll, pendingSelectedIds, serverSelectedIds])

  return (
    <>
      <div
        ref={triggerRef}
        className={`symbol-group symbol-hover users-symbol-group-compact flex-nowrap ${classes}`}
        data-task-users-selector
        onClick={handleClick}
      >
        {displayUsers &&
          displayUsers.map((user, i) => {
            return i < 3 ? (
              <OverlayTrigger
                key={`${i}-${user.name}`}
                placement='top'
                overlay={
                  <Tooltip id={`task-users-tt-${taskId ?? 'ph'}-${user.user_id}-${i}`}>
                    {user.name}
                  </Tooltip>
                }
              >
                <UserAvatar
                  name={user?.name ?? ''}
                  avatar={user?.avatar}
                  sizeClass={avatarSizeClass}
                  symbolLabelClass=''
                  imgClassName='object-fit-cover'
                  className={clsx(
                    'users-symbol-avatar-stack',
                    i === 0 && 'users-symbol-avatar-first',
                    isKanban && 'symbol-kanban'
                  )}
                  style={{cursor: isReadonly ? 'default' : 'pointer'}}
                />
              </OverlayTrigger>
            ) : null
          })}

        {displayUsers && displayUsers.length > 3 ? (
          <div
            className={`symbol ${avatarSizeClass} symbol-circle users-symbol-counter ${isKanban ? 'symbol-kanban' : ''}`}
          >
            <span
              className='symbol symbol-label bg-primary text-inverse-primary fw-bolder'
              style={{cursor: isReadonly ? 'default' : 'pointer'}}
            >
              {displayUsers.length}
            </span>
          </div>
        ) : null}

        {!isReadonly && (
          <div
            className={`symbol ${avatarSizeClass} symbol-circle symbol-size-md users-symbol-add ${isKanban ? 'symbol-kanban' : ''}`}
          >
            <span className='symbol symbol-label bg-primary text-inverse-primary fw-bolder'>+</span>
          </div>
        )}
      </div>
      {/* 
      //@ts-ignore */}
      <StyledPopper
        id={autocompleteValue?.toString()}
        open={open}
        anchorEl={effectiveAnchor}
        placement={placement}
        disablePortal={true}
        sx={!open ? { pointerEvents: 'none', visibility: 'hidden' } : undefined}
      >
        {open && (
        <ClickAwayListener onClickAway={handleClickAway} mouseEvent="onMouseDown">
          <div ref={popperContentRef} onMouseDown={(e) => e.stopPropagation()} data-task-users-selector>
            <Box
              sx={{
                borderBottom: `1px solid ${theme.palette.mode === 'light' ? '#eaecef' : '#30363d'}`,
                padding: '8px 10px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <span>{intl.formatMessage({id: 'FORM.INPUT.NAME.ADDUSER_TASK'})}</span>
              {pendingSelectedIds !== null && (() => {
                const hasChanges =
                  Array.from(pendingSelectedIds).some((id) => !serverSelectedIds.has(id)) ||
                  Array.from(serverSelectedIds).some((id) => !pendingSelectedIds.has(id))
                if (!hasChanges) return null
                return (
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <span style={{fontSize: 12, color: theme.palette.text.secondary}}>
                      {pendingSelectedIds.size} selecionado(s)
                    </span>
                    <button
                      type='button'
                      onClick={() => applyAndClose()}
                      disabled={isApplying}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: '4px 10px',
                        fontSize: 13,
                        fontWeight: 600,
                        color: '#2b2b2e',
                        backgroundColor: '#b5d44e',
                        border: 'none',
                        borderRadius: 6,
                        cursor: isApplying ? 'wait' : 'pointer',
                      }}
                    >
                      <DoneIcon sx={{fontSize: 16}} />
                      {isApplying ? 'Salvando...' : 'Salvar'}
                    </button>
                  </Box>
                )
              })()}
            </Box>
            <Autocomplete
              open
              multiple
              inputValue={searchInputValue}
              onInputChange={(_, value, reason) => {
                // O MUI dispara "reset" ao selecionar opção; nesse caso, manter o texto de busca
                // para permitir seleção sequencial de vários usuários filtrados.
                if (reason === 'reset') return
                setSearchInputValue(value ?? '')
              }}
              filterOptions={filterOptions}
              componentsProps={{ popper: { disablePortal: true } }}
              onClose={(event: React.ChangeEvent<{}>, reason: AutocompleteCloseReason) => {
                if (reason === 'escape') {
                  handleClose()
                }
              }}
              value={autocompleteValue}
              onChange={(event, newValue, reason, e) => {
                // Ignorar mudanças - o click é tratado diretamente no renderOption
                // Apenas manter para suporte a teclado (Enter em item focado)
                if (event.type === 'keydown') {
                  const option = e?.option as AutocompleteOption
                  if (option && 'isTeam' in option && option.isTeam) {
                    handleTeamToggle(option as TeamOption)
                  } else if (option) {
                    handleUserToggle(option as IconUserModel)
                  }
                }
              }}
              disableCloseOnSelect
              PopperComponent={PopperComponent}
              renderTags={() => null}
              noOptionsText='No labels'
              renderOption={(props, option, {selected}) => {
                // Extrair apenas props essenciais, removendo handlers do Autocomplete
                const { 
                  onClick: _onClick, 
                  onMouseOver: _onMouseOver,
                  onTouchStart: _onTouchStart,
                  ...liProps 
                } = props
                const opt = option as AutocompleteOption

                // Se for um time
                if ('isTeam' in opt && opt.isTeam) {
                  const teamOption = opt as TeamOption
                  const teamSelected = isTeamFullySelected(teamOption)

                  const onTeamClick = (e: React.MouseEvent) => {
                    e.stopPropagation()
                    e.preventDefault()
                    handleTeamToggle(teamOption)
                  }

                  return (
                    <li 
                      {...liProps} 
                      key={`team-${teamOption.id}`} 
                      data-user-selector-option='1'
                      style={{ cursor: 'pointer' }} 
                      // Usar mouseDown (ao invés de onClick) para garantir toggle,
                      // mesmo quando elementos internos (checkbox/avatar) interferem no click.
                      onMouseDown={onTeamClick}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                        <Checkbox
                          checked={teamSelected}
                          sx={{ p: 0.5 }}
                        />
                      </Box>
                      <Box
                        sx={{
                          flexGrow: 1,
                          fontWeight: 600,
                          fontSize: '13px',
                          color: theme.palette.mode === 'light' ? '#24292e' : '#c9d1d9',
                          cursor: 'pointer',
                        }}
                      >
                        {teamOption.name}
                        {teamOption.user && (
                          <Box
                            component='span'
                            sx={{
                              ml: 1,
                              fontSize: '12px',
                              color: theme.palette.mode === 'light' ? '#586069' : '#8b949e',
                              fontWeight: 400,
                            }}
                          >
                            ({teamOption.user.length} {teamOption.user.length === 1 ? 'membro' : 'membros'})
                          </Box>
                        )}
                      </Box>
                    </li>
                  )
                }

                // Se for um usuário
                const userOption = opt as IconUserModel
                const userSelected = isUserSelected(userOption.user_id)
                const teamId = userToTeamMap.get(Number(userOption.user_id))

                const onUserClick = (e: React.MouseEvent) => {
                  e.stopPropagation()
                  e.preventDefault()
                  handleUserToggle(userOption)
                }

                return (
                  <li
                    {...liProps}
                    key={`user-${userOption.user_id}`}
                    data-user-selector-option='1'
                    style={{
                      paddingLeft: teamId ? '40px' : '8px',
                      cursor: 'pointer',
                    }}
                    // Usar mouseDown (ao invés de onClick) para garantir toggle.
                    onMouseDown={onUserClick}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                      <Checkbox
                        checked={userSelected}
                        sx={{ p: 0.5 }}
                      />
                    </Box>

                    <Box sx={{mr: 1, mt: '2px', flexShrink: 0}}>
                      <UserAvatar
                        name={userOption?.name ?? ''}
                        avatar={userOption.avatar}
                        sizeClass='symbol-20px'
                        symbolLabelClass='me-2'
                        imgClassName='object-fit-cover'
                      />
                    </Box>
                    <Box
                      sx={{
                        flexGrow: 1,
                        cursor: 'pointer',
                        '& span': {
                          color: theme.palette.mode === 'light' ? '#586069' : '#8b949e',
                        },
                      }}
                    >
                      {userOption.name}
                    </Box>
                  </li>
                )
              }}
              options={combinedOptions}
              getOptionLabel={(option) => {
                if ('isTeam' in option && option.isTeam) {
                  return option.name
                }
                return (option as IconUserModel).name
              }}
              isOptionEqualToValue={(option, value) => {
                if ('isTeam' in option && option.isTeam) {
                  return 'isTeam' in value && value.isTeam && option.id === value.id
                }
                return (option as IconUserModel).user_id === (value as IconUserModel).user_id
              }}
              renderInput={(params) => (
                <StyledInput
                  inputRef={params.InputProps.ref}
                  inputProps={params.inputProps}
                  autoFocus
                  placeholder={intl.formatMessage({id: 'FORM.INPUT.NAME.SELECTUSER_TEAMS'})}
                />
              )}
            />
          </div>
        </ClickAwayListener>
        )}
      </StyledPopper>
    </>
  )
}

export {UsersSymbolGroupColumn}
